import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from "src/stateManagement/redux";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import moment from "moment";
import { tableStyles } from 'src/shared/util/tableStyles';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale, BarElement, TimeScale, LineController, BarController } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale, BarElement, TimeScale, LineController, BarController);

interface AccumulatedProductionProps {
  viewMode: 'table' | 'chart';
  setViewMode: (mode: 'table' | 'chart') => void;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  resetDatesRef: React.MutableRefObject<(() => void) | null>;
}

export const AccumulatedProduction = ({ viewMode, setViewMode, startDate, endDate, setStartDate, setEndDate, resetDatesRef }: AccumulatedProductionProps) => {
  const { productionAcumulated } = useSelector((state) => state.location);
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    // Ordenar por fecha descendente (más reciente primero)
    const data = productionAcumulated
      .map((item) => ({
        ...item,
      }))
      .sort((a, b) => {
        const dateA = a.productionDate ? moment(a.productionDate).valueOf() : 0;
        const dateB = b.productionDate ? moment(b.productionDate).valueOf() : 0;
        return dateB - dateA; // Descendente
      });
    setTableData(data);
  }, [productionAcumulated]);

  // Función para resetear fechas a min/max
  const resetDatesToMinMax = () => {
    if (tableData.length > 0) {
      const datesWithValues = tableData.filter(item => item.productionDate).map(item => moment(item.productionDate));
      if (datesWithValues.length > 0) {
        const minDate = moment.min(datesWithValues);
        const maxDate = moment.max(datesWithValues);
        setStartDate(minDate.format('YYYY-MM-DD'));
        setEndDate(maxDate.format('YYYY-MM-DD'));
      }
    }
  };

  // Exponer función de reset al componente padre
  useEffect(() => {
    resetDatesRef.current = resetDatesToMinMax;
    return () => {
      resetDatesRef.current = null;
    };
  }, [tableData]);

  // Inicializar fechas con min y max de los datos
  useEffect(() => {
    if (tableData.length > 0 && !startDate && !endDate) {
      resetDatesToMinMax();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const paginationModel = { page: 0, pageSize: 10 };

  const dataColumns: GridColDef[] = [
    {
      field: "productionDate",
      headerName: "Fecha de Producción",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const formattedDate = params.value
          ? moment(params.value).format("DD-MM-YY")
          : "";
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {formattedDate}
          </Typography>
        );
      },
    },
    {
      field: "workingDays",
      headerName: "Días Trabajados",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? params.value.toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "oilProduced",
      headerName: "Petróleo Producido",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "oilAcumulated",
      headerName: "Petróleo Acumulado",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "water",
      headerName: "Agua",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "waterAcumulated",
      headerName: "Agua Acumulada",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "gas",
      headerName: "Gas",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "gasAcumulated",
      headerName: "Gas Acumulado",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "averagePot",
      headerName: "Potencia Promedio",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
  ];

  // Prepare chart data - ordenado por fecha ascendente (más antigua a la izquierda, más reciente a la derecha)
  // Aplicar filtros de fecha si están definidos
  const sortedChartData = useMemo(() => {
    let filtered = tableData.filter((item) => item.productionDate);
    
    // Aplicar filtro de fecha inicio
    if (startDate) {
      const startMoment = moment(startDate).startOf('day');
      filtered = filtered.filter((item) => {
        const itemDate = moment(item.productionDate);
        return itemDate.isSameOrAfter(startMoment);
      });
    }
    
    // Aplicar filtro de fecha fin
    if (endDate) {
      const endMoment = moment(endDate).endOf('day');
      filtered = filtered.filter((item) => {
        const itemDate = moment(item.productionDate);
        return itemDate.isSameOrBefore(endMoment);
      });
    }
    
    // Ordenar por fecha ascendente
    return filtered.sort((a, b) => {
      const dateA = moment(a.productionDate).valueOf();
      const dateB = moment(b.productionDate).valueOf();
      return dateA - dateB; // Ascendente para gráfica
    });
  }, [tableData, startDate, endDate]);

  const chartData = {
    labels: sortedChartData.map((item) => moment(item.productionDate).format("DD-MM-YY")),
    datasets: [
      {
        label: "Petróleo Acumulado",
        data: sortedChartData.map((item) => 
          item.oilAcumulated ? parseFloat(item.oilAcumulated) : null
        ),
        borderColor: "rgb(129, 199, 132)", // Verde claro
        backgroundColor: "rgba(129, 199, 132, 0.2)",
        yAxisID: "y",
      },
      {
        label: "Potencial Promedio",
        data: sortedChartData.map((item) => 
          item.averagePot ? parseFloat(item.averagePot) : null
        ),
        borderColor: "rgb(27, 94, 32)", // Verde oscuro
        backgroundColor: "rgba(27, 94, 32, 0.2)",
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Fecha de Producción",
        },
      },
      y: {
        beginAtZero: true,
        position: "left" as const,
        title: {
          display: true,
          text: "Petróleo Acumulado",
        },
      },
      y1: {
        beginAtZero: true,
        position: "right" as const,
        title: {
          display: true,
          text: "Potencial Promedio",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {viewMode === 'table' ? (
        <DataGrid
          rows={tableData}
          columns={dataColumns}
          initialState={{
            pagination: { paginationModel },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          checkboxSelection={false}
          sx={{
            ...tableStyles,
            height: "100%",
          }}
          getRowId={(row) => row.id}
        />
      ) : (
        <Box sx={{ height: "calc(100vh - 300px)", p: 2 }}>
          {sortedChartData.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
              No hay datos disponibles para mostrar en el rango seleccionado
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
